/**
 * 69.100 step 4 (DECISIONS 406, GameConfig.finiteSupply): an APC's or M113's load (`supply.carrier`, core/carrierLoad.ts)
 * drawn on its roof — a rack of four crates that empty as it is drawn down: two of magazines (each half the full load's),
 * one of bandages and one of explosive small arms (each while any is left). Made the first time the carrier holds a load,
 * on its hull (it rides the suspension); never without the finite supply. Two draws a carrier: the rack, and the crates
 * (one instanced mesh, painted by kind). Presentation only.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { StructureState } from '../types';
import { SUPPLY_STOCK } from '../constants/logistics';
import type { VehicleModel } from './vehicleModels';

/**
 * Where each carrier's rack stands on its roof (the kit's units, a person 1.8: x across, the roof's height, z the rack's
 * middle along), clear of the hatches and the cupola: the BTR's left of its cupola, between the bay's roof hatches and the
 * driver's lid's swing; the M113's right of its cupola, ahead of the cargo hatch, behind the driver's.
 */
export const CARRIER_RACK = {
  wheeled: { x: -0.55, y: 2.26, z: -0.6 },
  tracked: { x: 0.9, y: 2.0, z: 0.05 },
  crate: { length: 0.4, width: 0.5, height: 0.3, gap: 0.04 },
  paint: { magazines: '#5a5d3c', bandages: '#e4dcc4', explosives: '#9a7a3a' },
};
const SLOTS = ['magazines', 'magazines', 'bandages', 'explosives'] as const;

export interface CarrierRack { group: THREE.Group; crates: THREE.InstancedMesh; shown: string }

/** Boxes merged into one vertex-coloured geometry: [w, h, d, x, y, z, colour]. */
function boxes(list: ReadonlyArray<[number, number, number, number, number, number, string]>): THREE.BufferGeometry {
  const parts = list.map(([w, h, d, x, y, z, c]) => {
    const g = new THREE.BoxGeometry(w, h, d).toNonIndexed().translate(x, y, z), col = new THREE.Color(c), n = g.attributes.position.count;
    g.setAttribute('color', new THREE.Float32BufferAttribute(Array.from({ length: n * 3 }, (_, k) => [col.r, col.g, col.b][k % 3]), 3));
    return g;
  });
  const g = mergeGeometries(parts, false)!;
  parts.forEach(q => q.dispose());
  return g;
}

let shared: { rack: THREE.BufferGeometry; crate: THREE.BufferGeometry; material: THREE.MeshLambertMaterial } | null = null;
/** The rack and the crate, shared by every carrier (built once). */
function parts(): NonNullable<typeof shared> {
  if (shared) return shared;
  const { length: l, width: w, height: h, gap } = CARRIER_RACK.crate, run = SLOTS.length * (l + gap) + 0.06, half = run / 2, rail = w / 2 + 0.03, steel = '#3a3d3a';
  const rack = boxes([
    [0.04, 0.05, run, -rail, 0.1, 0, steel], [0.04, 0.05, run, rail, 0.1, 0, steel],   // the side rails, a crate's lower third up
    ...[-half, 0, half].map((z): [number, number, number, number, number, number, string] => [2 * rail + 0.04, 0.03, 0.05, 0, 0.015, z, steel]),   // the cross bars on the roof
    ...[-1, 1].flatMap(sx => [-half, half].map((z): [number, number, number, number, number, number, string] => [0.04, 0.12, 0.04, sx * rail, 0.06, z, steel])),   // the corner posts
  ]);
  // a crate: its body (white: the instance's paint), a dark lid band, two handles at its ends
  const crate = boxes([
    [w, h, l, 0, h / 2, 0, '#ffffff'], [w + 0.01, 0.04, l + 0.01, 0, h - 0.06, 0, '#5c5c58'],
    [0.14, 0.03, 0.02, 0, h * 0.6, l / 2 + 0.01, '#3a3a36'], [0.14, 0.03, 0.02, 0, h * 0.6, -l / 2 - 0.01, '#3a3a36'],
  ]);
  shared = { rack, crate, material: new THREE.MeshLambertMaterial({ vertexColors: true }) };
  return shared;
}

/** Which of the four slots hold a crate for this load: the magazines' two by their share, the bandages', the explosives'. */
export function rackCrates(supply: NonNullable<StructureState['supply']>): Array<typeof SLOTS[number]> {
  const C = SUPPLY_STOCK.carrier, mags = Math.min(2, Math.ceil((2 * supply.magazines) / C.magazines - 1e-9));
  const x = supply.explosives, explosives = x ? x.he + x.smoke + x.gl40 + x.rocket : 0;
  return [...SLOTS.slice(0, Math.max(0, mags)), ...(supply.bandages > 0 ? ['bandages' as const] : []), ...(explosives > 0 ? ['explosives' as const] : [])];
}

/**
 * The carrier's load on its model: the rack made on its hull the first time it holds a load, its crates written when what
 * it holds changes (a compare a frame otherwise); hidden with no load (the switch off, or a carrier never loaded).
 */
export function setCarrierLoad(m: VehicleModel & { rack?: CarrierRack }, kind: 'wheeled' | 'tracked', supply: StructureState['supply']): void {
  if (!supply?.carrier) { if (m.rack) m.rack.group.visible = false; return; }
  if (!m.rack) {
    const P = parts(), at = CARRIER_RACK[kind], group = new THREE.Group();
    group.name = 'carrierRack';
    group.position.set(at.x, at.y, at.z);
    const rails = new THREE.Mesh(P.rack, P.material);
    rails.name = 'carrierRack:rails';
    group.add(rails);
    const crates = new THREE.InstancedMesh(P.crate, P.material, SLOTS.length);
    crates.name = 'carrierRack:crates';
    crates.count = 0;
    group.add(crates);
    m.hull.add(group);
    m.rack = { group, crates, shown: '' };
  }
  const r = m.rack, list = rackCrates(supply), shown = list.join();
  r.group.visible = true;
  if (shown === r.shown) return;
  r.shown = shown;
  const { length: l, gap } = CARRIER_RACK.crate, start = -((SLOTS.length - 1) / 2) * (l + gap), mat = new THREE.Matrix4(), col = new THREE.Color();
  // (the slots kept in their places: a magazines' crate gone leaves its slot bare, the others stay where they were)
  const slotOf: number[] = []; let mi = 0;
  for (const k of list) slotOf.push(k === 'magazines' ? mi++ : k === 'bandages' ? 2 : 3);
  list.forEach((k, i) => {
    r.crates.setMatrixAt(i, mat.makeTranslation(0, 0.02, start + slotOf[i] * (l + gap)));
    r.crates.setColorAt(i, col.set(CARRIER_RACK.paint[k]));
  });
  r.crates.count = list.length;
  r.crates.instanceMatrix.needsUpdate = true;
  if (r.crates.instanceColor) r.crates.instanceColor.needsUpdate = true;
  r.crates.computeBoundingSphere();
}
